<template>
  <nav class="bg-base-100 dark:bg-base-200 border-b">
    <div class="flex px-2 py-3 justify-between items-center container-lg">
      <div class="flex gap-3">
        <div class="flex flex-shrink-0 items-center">
          <router-link :to="routeManager.homePath()" class="tmi-logo h-10 w-40 bg-contain"></router-link>
        </div>
        <div class="hidden lg:flex lg:items-center">
          <details v-if=" showProductsMenuItem " id="products-menu" @mouseenter="showMenuOnFocus" @mouseleave="hideMenuOnBlur" class="dropdown dropdown-hover">
            <summary class="navbar-btn">{{ labelService.label( 'lblProducts' ) }}</summary>
            <ul class="menu dropdown-content w-72" @click="hideMenuOnBlur">
              <template v-if=" showProductBrochures ">
                <li class="menu-title text-jade pl-2 pb-0 uppercase">{{ labelService.label( 'lblAvailableServices' ) }}</li>
                <li v-for="product in productBrochures" :key="product.slug">
                  <router-link :to="routeManager.productRouteBySlug( product.slug )">
                    <img loading='lazy' v-if=" product.favicon " :src="product.favicon" class="rounded-lg w-6 h-6" alt="Product Logo">
                    <span> {{ product.name }}</span>
                  </router-link>
                </li>
              </template>
            </ul>
          </details>
          <router-link :to="routeManager.articleSearchPath()" class="navbar-btn">{{ labelService.label( 'lblArticles' ) }}</router-link>
          <router-link :to="routeManager.sectorEdgePath()" class="navbar-btn">{{ labelService.label( 'lblSectorEdge' ) }}</router-link>
          <router-link :to="aboutUsPath" class="navbar-btn">{{ labelService.label( 'lblAbout' ) }}</router-link>
          <router-link :to="contactUsPath" class="navbar-btn">{{ labelService.label( 'lblContact' ) }}</router-link>
        </div>
      </div>
      <div class="flex-c">
        <div>
          <div class="flex gap-2">
            <router-link class="lg:hidden btn btn-sm btn-primary uppercase" :to="routeManager.signUpPath()">
              {{ labelService.label( 'lblSignUp' ) }}
            </router-link>
            <router-link class="lg:hidden btn btn-sm btn-primary btn-outline uppercase" :to="routeManager.signInPath()">
              {{ labelService.label( 'lblLogIn' ) }}
            </router-link>
          </div>
        </div>
        <div @click="showMobileMenu = !showMobileMenu" class="mx-4 lg:hidden">
          <i class="pi" :class="showMobileMenu ? 'pi-times' : 'pi-bars'"></i>
        </div>
      </div>
      <div class="hidden lg:flex items-center">
        <div class="flex gap-2">
          <router-link class="btn btn-sm btn-primary no-underline uppercase" :to="routeManager.signUpPath()">
            {{ labelService.label( 'lblSignUp' ) }}
          </router-link>
          <router-link class="btn btn-sm btn-primary btn-outline no-underline uppercase" :to="routeManager.signInPath()">
            {{ labelService.label( 'lblLogIn' ) }}
          </router-link>
        </div>
        <DarkModeToggle />
      </div>
    </div>

    <!-- Mobile menu dropdown -->
    <div v-if=" showMobileMenu " class="lg:hidden shadow-inner" id="mobile-menu">
      <ul class="menu menu-lg lg:hidden">
        <li v-if=" showProductsMenuItem ">
          <details>
            <summary class="font-bold">{{ labelService.label( 'lblProducts' ) }}</summary>
            <ul>
              <template v-if=" showProductBrochures ">
                <li v-for="product in productBrochures" :key="product.slug">
                  <router-link :to="routeManager.productRouteBySlug( product.slug )">
                    <img loading='lazy' v-if=" product.favicon " :src="product.favicon" class="rounded-lg w-6 h-6" alt="Product Logo">
                    <span> {{ product.name }}</span>
                  </router-link>
                </li>
              </template>
            </ul>
          </details>
        </li>
        <li><router-link :to="routeManager.articleSearchPath()" class="font-bold">{{ labelService.label( 'lblArticles' ) }}</router-link></li>
        <li><router-link :to="routeManager.sectorEdgePath()" class="font-bold">{{ labelService.label( 'lblSectorEdge' ) }}</router-link></li>
        <li><router-link :to="aboutUsPath" class="font-bold">{{ labelService.label( 'lblAbout' ) }}</router-link></li>
        <li><router-link :to="contactUsPath" class="font-bold">{{ labelService.label( 'lblContact' ) }}</router-link></li>
      </ul>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { storeToRefs } from "pinia";
import { Router, useRouter } from "vue-router";
import routeManager from "@/router/route-manager";

import DarkModeToggle from '@/components/layout/DarkModeToggle.vue';

import type { Product } from "@/types/entities";
import { useProductsStore } from "@/stores/Products";
const productsStore = useProductsStore();
const { availableProducts } = storeToRefs( productsStore );

import { useMenuPermissions } from "@/composables/menu-permissions";
const { showProductsMenuItem } = useMenuPermissions();

import type { LabelService } from "@/composables/label-service";
import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";

const labelService = inject< LabelService >( labelServiceSymbol ) as LabelService;

const router: Router = useRouter();

const aboutUsPath = ref< string >( routeManager.aboutUsPath() );
const contactUsPath = ref< string >( routeManager.contactUsPath() );

const hasAvailableProducts = computed< boolean >( () => availableProducts.value?.length > 0 );

const showProductBrochures = computed< boolean >( () => hasAvailableProducts.value );
const productBrochures = computed< Product[] >( () => {
  let products: Product[] = [];
  availableProducts.value.forEach( ( product: Product ) => products.push( product ) );
  return products;
} );

const showMobileMenu = ref< boolean >( false );
router.beforeEach( () => {
  showMobileMenu.value = false;
} );

function hideMenuOnBlur ( event: MouseEvent ) {
  if ( event.target ) {
    ( event.target as Element ).removeAttribute( "open" );
  }
}

function showMenuOnFocus ( event: Event ) {
  if ( event.target ) {
    ( event.target as Element ).setAttribute( "open", "" );
  }
}
</script>

<style>
a.tmi-logo {
  background-image: url("/img/logos/logo-green.svg");
  background-repeat: no-repeat;
}

[data-theme="dark"] a.tmi-logo {
  background-image: url("/img/logos/logo-white.svg")
}
</style>
